import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import LandingLayout from "../components/layout/LandingLayout";
import DscsaHero from "../components/lp/DscsaHero";
import DscsaQuiz from "../components/lp/DscsaQuiz";
import HowItWorks from "../components/lp/HowItWorks";
import {
  trackEvent,
  trackServerEvent,
  getUtmParams,
  trackButtonClick,
  setupPageTimeTracking,
  getVisitorId,
  setLandingAttribution,
  trackLandingEvent,
} from "../lib/analytics";
import { DEFAULT_LANDING_PAGE_CONTENT, type LandingPageContent } from "@shared/landingPages";
import ManagedContentSections from "../components/lp/ManagedContentSections";

const LP_PATH = "/lp/dscsa-compliance";
const DEFAULT_ROBOTS = "index,follow,max-image-preview:large,max-snippet:-1";
const MANAGED_LANDING_ROBOTS = DEFAULT_ROBOTS;

/**
 * DscsaLandingPage — standalone conversion landing page at /lp/dscsa-compliance.
 *
 * Uses LandingLayout (no Navbar / Footer) so nothing competes with the CTA.
 * Composed of:
 *   1. DscsaHero   — above-the-fold hero + ScriptTrack mockup
 *   2. DscsaQuiz   — 2-minute DSCSA risk assessment funnel
 *   3. HowItWorks  — 3-step onboarding flow
 */
export default function DscsaLandingPage() {
  // Capture UTM params on first render; stored in sessionStorage by getUtmParams
  // so they survive form submissions that happen later in the same session.
  const utmRef = useRef(getUtmParams());
  const [content, setContent] = useState<LandingPageContent>(DEFAULT_LANDING_PAGE_CONTENT["dscsa-compliance"]);

  useEffect(() => {
    fetch(`/api/landing-pages/dscsa-compliance?visitorId=${encodeURIComponent(getVisitorId())}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error("managed page unavailable")))
      .then((data) => {
        if (data?.content) setContent(data.content as LandingPageContent);
        if (data?.assignment) {
          setLandingAttribution({
            landingPage: LP_PATH,
            variantKey: data.assignment.variantKey || "control",
            experimentId: data.assignment.experimentId || undefined,
          });
        } else {
          setLandingAttribution({ landingPage: LP_PATH, variantKey: "control" });
        }
        trackLandingEvent("landing_page_view", LP_PATH);
      })
      .catch(() => {
        setLandingAttribution({ landingPage: LP_PATH, variantKey: "control" });
        trackLandingEvent("landing_page_view", LP_PATH);
      });
    return setupPageTimeTracking(LP_PATH);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // The app shell's default robots tag is not Helmet-managed. Update that
    // single tag in place so client-side navigation cannot leave conflicting
    // index and noindex directives in document.head.
    const existingTags = Array.from(
      document.head.querySelectorAll<HTMLMetaElement>('meta[name="robots"]'),
    );
    const robotsTag = existingTags[0] ?? document.createElement("meta");
    if (existingTags.length === 0) {
      robotsTag.name = "robots";
      document.head.appendChild(robotsTag);
    }
    existingTags.slice(1).forEach((tag) => tag.remove());
    robotsTag.content = MANAGED_LANDING_ROBOTS;

    return () => {
      robotsTag.content = DEFAULT_ROBOTS;
    };
  }, []);

  function handleQuizStep(step: number, answer: string) {
    trackEvent("quiz_step", { step, answer, page: LP_PATH });
    trackServerEvent("dscsa_lp_quiz_step", LP_PATH, { step, answer });
  }

  function handleLeadSubmit() {
    const utm = utmRef.current;
    trackEvent("generate_lead", {
      method: "dscsa_quiz",
      page: LP_PATH,
      ...(utm.utmSource ? { utm_source: utm.utmSource } : {}),
      ...(utm.utmMedium ? { utm_medium: utm.utmMedium } : {}),
      ...(utm.utmCampaign ? { utm_campaign: utm.utmCampaign } : {}),
    });
    trackServerEvent("dscsa_lp_lead_submit", LP_PATH, {
      method: "dscsa_quiz",
    });
  }

  function handleHeroCtaClick() {
    trackLandingEvent("landing_page_cta_click", LP_PATH, { buttonLabel: content.ctaText, buttonLocation: "hero-cta" });
    trackButtonClick(content.ctaText, "hero-cta");
  }

  function handleHowItWorksCtaClick() {
    trackLandingEvent("landing_page_cta_click", LP_PATH, { buttonLabel: content.ctaText, buttonLocation: "how-it-works-cta" });
    trackButtonClick(content.ctaText, "how-it-works-cta");
  }

  return (
    <LandingLayout>
      <Helmet>
        <title>DSCSA Compliance for Pharmacies — Premier Pharma</title>
        <meta
          name="description"
          content="Start a DSCSA workflow with a free Premier Pharma account and included starter compliance software. Credentials are reviewed before activation."
        />
        <link rel="canonical" href={`https://premierpharma.com${LP_PATH}`} />
      </Helmet>

      {/* Hero */}
      <DscsaHero onCtaClick={handleHeroCtaClick} content={content} />

      {/* Quiz funnel */}
      <DscsaQuiz onStepAdvance={handleQuizStep} onLeadSubmit={handleLeadSubmit} />

      <ManagedContentSections content={content} />

      {/* How It Works */}
      <HowItWorks onCtaClick={handleHowItWorksCtaClick} content={content} />
    </LandingLayout>
  );
}
